import { useState, useEffect, useCallback } from "react";

interface UseExitIntentOptions {
  delayMs?: number;
  mobileDelayMs?: number;
  threshold?: number;
}

export const useExitIntent = ({
  delayMs = 5000,
  mobileDelayMs = 30000,
  threshold = 10,
}: UseExitIntentOptions = {}) => {
  const [showPopup, setShowPopup] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);

  const hasShownPopup = useCallback(() => {
    return sessionStorage.getItem("exitIntentShown") === "true";
  }, []);

  const hasDismissedStickyBar = useCallback(() => {
    return sessionStorage.getItem("stickyBarDismissed") === "true";
  }, []);

  const isMobile = useCallback(() => {
    return window.matchMedia("(max-width: 768px)").matches;
  }, []);

  const markAsShown = useCallback(() => {
    sessionStorage.setItem("exitIntentShown", "true");
  }, []);

  const triggerPopup = useCallback(() => {
    if (!hasShownPopup() && !hasDismissedStickyBar()) {
      setShowPopup(true);
      markAsShown();
    }
  }, [hasShownPopup, hasDismissedStickyBar, markAsShown]);

  const dismissPopup = useCallback(() => {
    setShowPopup(false);
  }, []);

  useEffect(() => {
    // Don't enable if already shown or sticky bar dismissed
    if (hasShownPopup() || hasDismissedStickyBar()) {
      return;
    }

    // Enable detection after initial delay
    const enableTimer = setTimeout(() => {
      setIsEnabled(true);
    }, delayMs);

    return () => clearTimeout(enableTimer);
  }, [delayMs, hasShownPopup, hasDismissedStickyBar]);

  useEffect(() => {
    if (!isEnabled) return;

    // Mobile fallback: show after delay
    if (isMobile()) {
      const mobileTimer = setTimeout(() => {
        triggerPopup();
      }, mobileDelayMs - delayMs); // Subtract initial delay already waited

      return () => clearTimeout(mobileTimer);
    }

    // Desktop: detect mouse leaving viewport from top
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY < threshold) {
        triggerPopup();
      }
    };

    document.addEventListener("mouseout", handleMouseLeave);

    return () => {
      document.removeEventListener("mouseout", handleMouseLeave);
    };
  }, [isEnabled, isMobile, mobileDelayMs, delayMs, threshold, triggerPopup]);

  return {
    showPopup,
    dismissPopup,
    triggerPopup,
  };
};
